import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  ArrowRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  LightBulbIcon,
  SparklesIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  MapPinIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline';

import handcheckImg from '../../assets/img/Partner image/handcheck.jpg';
import rijanPhoto from '../../assets/img/team/rijan.png';
import kriteshPhoto from '../../assets/img/team/kritesh.png';
import nirvanPhoto from '../../assets/img/team/nirvan.png';
import roshaniPhoto from '../../assets/img/team/roshani.png';

function SectionLabel({ children }) {
  return (
    <span className="inline-block px-4 py-1.5 mb-4 text-xs font-semibold tracking-widest uppercase rounded-full hero-glass-tag text-[#2563EB]">
      {children}
    </span>
  );
}

const stats = [
  { id: 1, label: 'Years of Experience', value: 9, suffix: '+' },
  { id: 2, label: 'Projects Completed', value: 51, suffix: '+' },
  { id: 3, label: 'Happy Clients', value: 41, suffix: '+' },
  { id: 4, label: 'Team Members', value: 21, suffix: '+' },
  { id: 5, label: 'Countries Served', value: 4, suffix: '+' },
  { id: 6, label: 'Industry Awards', value: 1, suffix: '' },
];

const values = [
  {
    id: 1,
    name: 'Innovation',
    icon: LightBulbIcon,
    description: 'We push boundaries with emerging tech—AI, cloud, and automation—to solve complex enterprise challenges.',
    color: 'from-amber-400/20 to-orange-500/10',
  },
  {
    id: 2,
    name: 'Excellence',
    icon: SparklesIcon,
    description: 'Quality is non-negotiable—from architecture and code to communication and delivery standards.',
    color: 'from-blue-400/20 to-cyan-500/10',
  },
  {
    id: 3,
    name: 'Collaboration',
    icon: UserGroupIcon,
    description: 'We partner deeply with clients and across disciplines to achieve outcomes neither could alone.',
    color: 'from-violet-400/20 to-purple-500/10',
  },
  {
    id: 4,
    name: 'Integrity',
    icon: ShieldCheckIcon,
    description: 'Transparency, ethics, and security-first thinking build trust that lasts beyond a single project.',
    color: 'from-emerald-400/20 to-teal-500/10',
  },
];

const teamMembers = [
  {
    id: 1,
    name: 'Rijan Shrestha',
    role: 'Flutter Developer',
    initials: 'RS',
    color: 'from-blue-500 to-cyan-500',
    bio: 'Builds polished cross-platform mobile experiences with performance and usability at the core.',
  },
  {
    id: 2,
    name: 'Kritesh Shrestha',
    role: 'Full Stack Developer',
    initials: 'KS',
    color: 'from-violet-500 to-purple-500',
    bio: 'Delivers scalabBuilds modern full-stack web applications with scalable frontend and backend solutions.',
  },
  {
    id: 3,
    name: 'Nirvan Shrestha',
    role: 'Marketing Analyst',
    initials: 'NS',
    color: 'from-emerald-500 to-teal-500',
    bio: 'Turns data into actionable strategies that amplify brand reach and client success.',
  },
  {
    id: 4,
    name: 'Roshani Shrestha',
    role: 'Development Lead',
    initials: 'CS',
    color: 'from-rose-500 to-pink-500',
    bio: 'Leads engineering teams with a focus on architecture, delivery excellence, and mentorship.',
  },
];

const teamPhotoByName = {
  'Rijan Shrestha': rijanPhoto,
  'Kritesh Shrestha': kriteshPhoto,
  'Nirvan Shrestha': nirvanPhoto,
  'Roshani Shrestha': roshaniPhoto,
};

const timeline = [
  { id: 1, year: '2015', title: 'International Expansion', description: 'Began serving clients beyond Nepal, establishing our regional technology footprint.' },
  { id: 2, year: '2017', title: 'Company Founded', description: 'Started as a focused team delivering web and software solutions for local enterprises.' },
  { id: 3, year: '2018', title: 'First Major Enterprise Client', description: 'Scaled delivery capabilities and expanded into cloud and security services.' },
  { id: 4, year: '2020', title: 'Remote-First Culture', description: 'Adopted distributed collaboration to attract top talent and serve global clients.' },
  { id: 5, year: '2023', title: 'AI & Cybersecurity Division', description: 'Launched specialized practices in AI/ML, DevOps, and enterprise security solutions.' },
];

const testimonials = [
  {
    id: 1,
    name: 'Rujesh Shrestha',
    position: 'CTO',
    company: 'Next Int Company',
    initials: 'RS',
    color: 'from-blue-500 to-cyan-500',
    rating: 5,
    quote: 'Nisunajni transformed our digital infrastructure. Their technical depth and strategic approach exceeded every expectation.',
  },
  {
    id: 2,
    name: 'Rebecca Torres',
    position: 'CEO',
    company: 'Innovate Solutions',
    initials: 'RT',
    color: 'from-violet-500 to-purple-500',
    rating: 5,
    quote: 'They translated our business goals into elegant, scalable systems. A true long-term technology partner.',
  },
  {
    id: 3,
    name: 'Thomas Zhang',
    position: 'Marketing Director',
    company: 'EcoSmart',
    initials: 'TZ',
    color: 'from-emerald-500 to-teal-500',
    rating: 4,
    quote: 'From consultation to launch, the process was seamless. Our new platform lifted conversions significantly.',
  },
];

function CountUp({ end, suffix = '' }) {
  const [count, setCount] = useState(0);
  const [ref, inView] = useInView({ threshold: 0.3, triggerOnce: true });

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const duration = 1800;
    const step = Math.max(1, Math.floor(end / (duration / 16)));
    const timer = setInterval(() => {
      start += step;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [inView, end]);

  return (
    <span ref={ref}>
      {count}
      {suffix}
    </span>
  );
}

function StarRating({ count }) {
  return (
    <div className="flex gap-0.5 justify-center mb-6">
      {Array.from({ length: 5 }).map((_, i) => (
        <svg key={i} className={`w-5 h-5 ${i < count ? 'text-amber-400' : 'text-slate-200'}`} fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

export default function About() {
  const [refHero, inViewHero] = useInView({ threshold: 0.1, triggerOnce: true });
  const [refMission, inViewMission] = useInView({ threshold: 0.1, triggerOnce: true });
  const [refStats, inViewStats] = useInView({ threshold: 0.08, triggerOnce: true });
  const [refValues, inViewValues] = useInView({ threshold: 0.08, triggerOnce: true });
  const [refTeam, inViewTeam] = useInView({ threshold: 0.08, triggerOnce: true });
  const [refTimeline, inViewTimeline] = useInView({ threshold: 0.08, triggerOnce: true });
  const [refTestimonials, inViewTestimonials] = useInView({ threshold: 0.08, triggerOnce: true });
  const [refCta, inViewCta] = useInView({ threshold: 0.1, triggerOnce: true });

  const [testimonialIndex, setTestimonialIndex] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setTestimonialIndex((p) => (p + 1) % testimonials.length), 6000);
    return () => clearInterval(t);
  }, []);

  const activeTestimonial = testimonials[testimonialIndex];

  return (
    <div className="bg-white dark:bg-gray-900 overflow-x-hidden">
      {/* Hero */}
      <section
        ref={refHero}
        className="relative pt-28 pb-20 md:pt-32 md:pb-28 px-6 bg-gradient-to-b from-white via-slate-50/90 to-gray-100/80 overflow-hidden"
      >
        <div className="absolute top-0 right-0 w-[28rem] h-[28rem] bg-blue-400/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-cyan-300/10 rounded-full blur-3xl" />

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={inViewHero ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <SectionLabel>About Nisunajni Group</SectionLabel>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
            <span className="text-slate-900 dark:text-white">We Build </span>
            <span className="bg-gradient-to-r from-[#2563EB] via-blue-500 to-cyan-500 bg-clip-text text-transparent">
              Digital Futures
            </span>
          </h1>
          <p className="text-lg md:text-xl text-slate-600 dark:text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed">
            A passionate team of engineers, designers, and strategists helping enterprises thrive through secure, innovative technology.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-[#2563EB] to-blue-500 text-white font-semibold rounded-xl hero-glow hover:-translate-y-0.5 transition-all"
            >
              Work With Us <ArrowRightIcon className="w-5 h-5" />
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl border border-slate-200 dark:border-gray-600 text-slate-700 dark:text-gray-200 font-medium hero-glass-tag hover:border-[#2563EB]/40 hover:text-[#2563EB] transition-all"
            >
              Our Services
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Mission */}
      <section ref={refMission} className="py-20 md:py-28 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              animate={inViewMission ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              <SectionLabel>Our Story</SectionLabel>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-6 leading-tight">
                Technology Partners for{' '}
                <span className="text-[#2563EB]">Ambitious Organizations</span>
              </h2>
              <p className="text-slate-600 dark:text-gray-300 mb-4 leading-relaxed">
                Nisunajni Group delivers enterprise IT solutions—from cloud and cybersecurity to AI and custom software—for organizations that demand reliability, security, and innovation.
              </p>
              <p className="text-slate-600 dark:text-gray-300 mb-8 leading-relaxed">
                Based in Nepal with a growing global footprint, we combine local dedication with world-class engineering practices to transform how businesses operate in the digital age.
              </p>
              <div className="flex flex-wrap gap-3">
                {['ISO-aligned processes', 'Agile delivery', '24/7 support mindset'].map((tag) => (
                  <span key={tag} className="px-4 py-2 text-sm font-medium rounded-full hero-glass-tag text-slate-600 dark:text-gray-300">
                    {tag}
                  </span>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 24 }}
              animate={inViewMission ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="relative"
            >
              <div className="absolute -inset-4 bg-gradient-to-br from-[#2563EB]/20 to-cyan-400/20 rounded-3xl blur-2xl opacity-60" />
              <div className="relative hero-glass-card rounded-3xl overflow-hidden border border-slate-200/90 dark:border-gray-700 shadow-xl">
                <img src={handcheckImg} alt="Team collaboration" className="w-full h-80 md:h-[420px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 flex items-center gap-3 p-4 rounded-2xl bg-white/90 dark:bg-gray-800/90 backdrop-blur-md border border-white/50 shadow-lg">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#2563EB] to-cyan-500 flex items-center justify-center flex-shrink-0">
                    <MapPinIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-900 dark:text-white text-sm">Kathmandu, Nepal</p>
                    <p className="text-xs text-slate-500 dark:text-gray-400">Serving clients worldwide</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section ref={refStats} className="py-20 px-6 bg-slate-50/80 dark:bg-gray-800/50">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={inViewStats ? { opacity: 1, y: 0 } : {}} className="text-center mb-14">
            <SectionLabel>By The Numbers</SectionLabel>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">
              Our Impact in <span className="text-[#2563EB]">Numbers</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {stats.map((stat, i) => (
              <motion.div
                key={stat.id}
                initial={{ opacity: 0, y: 20 }}
                animate={inViewStats ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: i * 0.06 }}
                whileHover={{ y: -4 }}
                className="hero-glass-card rounded-2xl p-6 text-center group hover:shadow-lg hover:shadow-blue-500/10 transition-all"
              >
                <p className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white group-hover:text-[#2563EB] transition-colors">
                  <CountUp end={stat.value} suffix={stat.suffix} />
                </p>
                <p className="text-xs md:text-sm text-slate-500 dark:text-gray-400 mt-2 font-medium">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section ref={refValues} className="py-20 md:py-28 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={inViewValues ? { opacity: 1, y: 0 } : {}} className="text-center mb-14">
            <SectionLabel>Our Foundation</SectionLabel>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">
              Core <span className="text-[#2563EB]">Values</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {values.map((value, i) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={value.id}
                  initial={{ opacity: 0, y: 24 }}
                  animate={inViewValues ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: i * 0.08 }}
                  whileHover={{ y: -6 }}
                  className="group relative"
                >
                  <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${value.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                  <div className="relative hero-glass-card rounded-2xl p-8 flex gap-6 h-full">
                    <div className="w-14 h-14 rounded-2xl bg-[#2563EB]/10 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                      <Icon className="w-7 h-7 text-[#2563EB]" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2 group-hover:text-[#2563EB] transition-colors">
                        {value.name}
                      </h3>
                      <p className="text-slate-600 dark:text-gray-300 text-sm leading-relaxed">{value.description}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team */}
      <section ref={refTeam} className="py-20 md:py-28 px-6 bg-gradient-to-b from-slate-50/80 to-white dark:from-gray-800 dark:to-gray-900">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={inViewTeam ? { opacity: 1, y: 0 } : {}} className="text-center mb-14">
            <SectionLabel>Meet The Team</SectionLabel>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">
              The <span className="text-[#2563EB]">People</span> Behind Our Success
            </h2>
            <p className="text-slate-600 dark:text-gray-300 max-w-2xl mx-auto mt-4">
              Experts in engineering, design, and strategy—united by a commitment to client success.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {teamMembers.map((member, i) => (
              <motion.div
                key={member.id}
                initial={{ opacity: 0, y: 28 }}
                animate={inViewTeam ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: i * 0.08 }}
                whileHover={{ y: -8 }}
                className="group hero-glass-card rounded-2xl overflow-hidden hover:shadow-xl hover:shadow-blue-500/15 transition-all duration-500"
              >
                <div className="relative px-4 pt-6">
                  <div className="relative h-72 md:h-80 overflow-hidden rounded-xl bg-slate-100 dark:bg-gray-700">
                    <img
                      src={teamPhotoByName[member.name]}
                      alt={member.name}
                      className="w-full h-full object-cover object-[center_top] transition-transform duration-700 group-hover:scale-[1.03]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>
                </div>
                <div className="px-6 pb-6 pt-5">
                  <h3 className="font-bold text-slate-900 dark:text-white">{member.name}</h3>
                  <p className="text-sm text-[#2563EB] font-medium mb-3">{member.role}</p>
                  <p className="text-sm text-slate-600 dark:text-gray-300 leading-relaxed">{member.bio}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/career"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl border-2 border-[#2563EB]/30 text-[#2563EB] font-semibold hover:bg-[#2563EB] hover:text-white transition-all"
            >
              Join Our Team <ArrowRightIcon className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section ref={refTimeline} className="py-20 md:py-28 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={inViewTimeline ? { opacity: 1, y: 0 } : {}} className="text-center mb-14">
            <SectionLabel>Our Journey</SectionLabel>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">
              Company <span className="text-[#2563EB]">Timeline</span>
            </h2>
          </motion.div>

          <div className="relative">
            <div className="absolute left-4 md:left-1/2 md:-translate-x-px top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#2563EB] via-cyan-400 to-blue-200" />

            <div className="space-y-8">
              {timeline.map((item, i) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }}
                  animate={inViewTimeline ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: i * 0.1 }}
                  className={`relative flex flex-col md:flex-row gap-6 md:gap-0 ${
                    i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className="hidden md:block md:w-1/2" />
                  <div
                    className={`absolute left-4 md:left-1/2 w-4 h-4 rounded-full bg-[#2563EB] border-4 border-white dark:border-gray-900 shadow-md -translate-x-1/2 md:-translate-x-1/2 top-6 z-10`}
                  />
                  <div className={`pl-12 md:pl-0 md:w-1/2 ${i % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12 md:text-left'}`}>
                    <div className="hero-glass-card rounded-2xl p-6 hover:shadow-lg transition-shadow">
                      <span className="inline-block px-3 py-1 text-xs font-bold rounded-full bg-[#2563EB]/10 text-[#2563EB] mb-2">
                        {item.year}
                      </span>
                      <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">{item.title}</h3>
                      <p className="text-sm text-slate-600 dark:text-gray-300">{item.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section ref={refTestimonials} className="py-20 md:py-28 px-6 bg-slate-50/80 dark:bg-gray-800/50">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={inViewTestimonials ? { opacity: 1, y: 0 } : {}} className="text-center mb-12">
            <SectionLabel>Client Voices</SectionLabel>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white">What Clients Say</h2>
          </motion.div>

          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={testimonialIndex}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.4 }}
                className="hero-glass-card rounded-3xl p-8 md:p-12 text-center shadow-lg"
              >
                <StarRating count={activeTestimonial.rating} />
                <p className="text-lg md:text-xl text-slate-700 dark:text-gray-200 leading-relaxed mb-8 italic">
                  &ldquo;{activeTestimonial.quote}&rdquo;
                </p>
                <div className={`w-14 h-14 mx-auto rounded-full bg-gradient-to-br ${activeTestimonial.color} flex items-center justify-center text-white font-bold mb-3`}>
                  {activeTestimonial.initials}
                </div>
                <h4 className="font-bold text-slate-900 dark:text-white">{activeTestimonial.name}</h4>
                <p className="text-sm text-[#2563EB]">
                  {activeTestimonial.position}, {activeTestimonial.company}
                </p>
              </motion.div>
            </AnimatePresence>

            <button
              type="button"
              onClick={() => setTestimonialIndex((p) => (p - 1 + testimonials.length) % testimonials.length)}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 md:-translate-x-14 p-3 rounded-full hero-glass-card hover:hero-glow transition-all"
              aria-label="Previous"
            >
              <ChevronLeftIcon className="w-5 h-5 text-slate-700" />
            </button>
            <button
              type="button"
              onClick={() => setTestimonialIndex((p) => (p + 1) % testimonials.length)}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 md:translate-x-14 p-3 rounded-full hero-glass-card hover:hero-glow transition-all"
              aria-label="Next"
            >
              <ChevronRightIcon className="w-5 h-5 text-slate-700" />
            </button>

            <div className="flex justify-center gap-2 mt-8">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setTestimonialIndex(i)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    i === testimonialIndex ? 'w-8 bg-[#2563EB]' : 'w-2 bg-slate-300'
                  }`}
                  aria-label={`Testimonial ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA — original site style */}
      <section
        ref={refCta}
        className="py-20 px-6 bg-gradient-to-r from-blue-600 to-purple-600 relative overflow-hidden"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inViewCta ? { opacity: 1, y: 0 } : {}}
          className="max-w-4xl mx-auto text-center relative z-10"
        >
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white/15 backdrop-blur-md border border-white/20 mb-6">
            <RocketLaunchIcon className="w-7 h-7 text-white" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Transform Your Business?</h2>
          <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Let&apos;s discuss how our expertise can help you achieve your goals through innovative technology.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="px-8 py-4 bg-white text-blue-600 font-medium rounded-lg shadow-lg hover:bg-gray-100 transition">
              Get in Touch
            </Link>
            <Link to="/services" className="px-8 py-4 border-2 border-white text-white font-medium rounded-lg hover:bg-white/10 transition">
              Explore Our Services
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
